import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

interface PTCacheState {
    /**
     * Cache of Product Type nodes by family ID
     * Key: familyId, Value: array of PT nodes
     */
    cachedFamilyPTs: Record<string, ProjectElementNode[]>

    /**
     * Store Product Types for a specific family
     */
    setCachedFamilyPTs: (familyId: string, pts: ProjectElementNode[]) => void

    /**
     * Get cached Product Types for a specific family
     */
    getCachedFamilyPTs: (familyId: string) => ProjectElementNode[] | undefined

    /**
     * Clear cache for a specific family
     */
    clearFamilyCache: (familyId: string) => void

    /**
     * Clear entire cache
     */
    clearAllCache: () => void
}

/**
 * Zustand store for caching Product Type nodes when "Added To Project" filter is active.
 *
 * This store caches ALL Product Types for a family when the family is selected and the filter is ON.
 * During PT activation, the cache is checked and PTs are added to elementList if needed,
 * allowing the synchronous detection logic to find them.
 */
export const usePTCacheStore = create<PTCacheState>((set, get) => ({
    cachedFamilyPTs: {},

    setCachedFamilyPTs: (familyId: string, pts: ProjectElementNode[]) => {
        set((state) => ({
            cachedFamilyPTs: {
                ...state.cachedFamilyPTs,
                [familyId]: pts,
            },
        }))
    },

    getCachedFamilyPTs: (familyId: string) => {
        return get().cachedFamilyPTs[familyId]
    },

    clearFamilyCache: (familyId: string) => {
        set((state) => {
            const newCache = { ...state.cachedFamilyPTs }
            delete newCache[familyId]
            return { cachedFamilyPTs: newCache }
        })
    },

    clearAllCache: () => {
        set({ cachedFamilyPTs: {} })
    },
}))

/**
 * Shallow selector hook for PT cache store to prevent unnecessary re-renders.
 * Only re-renders when the selected values actually change (shallow comparison).
 *
 * @example
 * const { setCachedFamilyPTs, clearAllCache } = usePTCacheStoreShallowSelector((state) => ({
 *     setCachedFamilyPTs: state.setCachedFamilyPTs,
 *     clearAllCache: state.clearAllCache,
 * }))
 */
export const usePTCacheStoreShallowSelector = <T>(
    selector: (state: PTCacheState) => T
): T => usePTCacheStore(useShallow(selector))
